import {
  DesktopOutlined,
  LoadingOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { App, Avatar, Dropdown, Tooltip, theme } from 'antd';
import { useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/auth-context';
import { fullName, initials, roleLabel } from '@/lib/format';
import { useThemeMode, type ThemePreference } from '@/theme/theme-provider';

/**
 * Who you are signed in as, at the foot of the sidebar where a desktop app
 * puts it — and the theme control, because a colour scheme is a preference
 * about this person's own session, which is what everything else in here is.
 *
 * Lifted out of `AppLayout` when it grew a third theme option. It was half of
 * that file and none of the layout.
 *
 * It is a panel rather than an antd `Menu` for the same reason
 * `PharmacyScopePicker` is: these two controls sit one above the other in the
 * same corner of the same column, and a menu drawn in antd's idiom directly
 * above a panel that is not reads as two different products. Owning the markup
 * also fixes what the `Menu` version could not — the identity block was held in
 * a `disabled` item to stop it being clickable, which painted the one line you
 * actually read in the disabled grey.
 */

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: ComponentType }[] = [
  { value: 'light', label: 'Light', icon: SunOutlined },
  { value: 'dark', label: 'Dark', icon: MoonOutlined },
  { value: 'system', label: 'System', icon: DesktopOutlined },
];

export function AccountMenu({ collapsed }: { collapsed?: boolean }) {
  const { token } = theme.useToken();
  const { user, signOut } = useAuth();
  const { mode, preference, setPreference } = useThemeMode();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const name = user ? fullName(user) : '—';
  const contact = user?.email ?? user?.phone ?? '';
  const roles = user?.roles ?? [];

  /*
   * No reset afterwards, and no failure branch: `signOut` clears the local
   * session first and swallows a failed revoke, so it always resolves and this
   * component is unmounted by the redirect that follows. The spinner runs until
   * the login screen replaces it, which is exactly as long as it should.
   */
  const handleSignOut = () => {
    setSigningOut(true);
    void signOut().then(() => {
      message.success('Signed out');
      navigate('/login', { replace: true });
    });
  };

  const panel = (
    <div className="acct-panel">
      <div className="acct-identity">
        <Avatar style={{ backgroundColor: token.colorPrimary }} size={38}>
          {user ? initials(user) : '?'}
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="acct-identity-name">{name}</div>
          {/* Full ink and muted ink — both readable. See the note on the file. */}
          {contact ? <div className="acct-identity-contact">{contact}</div> : null}
          {roles.length > 0 ? (
            <div className="acct-roles">
              {roles.map((role) => (
                <span key={role} className="acct-role">
                  {roleLabel(role)}
                </span>
              ))}
            </div>
          ) : (
            <div className="acct-roles">
              <span className="acct-role">No roles</span>
            </div>
          )}
        </div>
      </div>

      <div className="acct-section">
        {/*
          * A radio group, not a segmented div. Arrow keys move between the
          * three and select as they go, the group announces itself as one
          * control, and "which is chosen" is `checked` rather than a class —
          * none of which the previous `Segmented` could say to a screen reader.
          */}
        <div role="radiogroup" aria-labelledby="acct-theme-label">
          <span className="acct-section-label" id="acct-theme-label">
            Appearance
          </span>
          <div className="theme-switch">
            {THEME_OPTIONS.map((option) => (
              <label key={option.value} className="theme-opt">
                <input
                  type="radio"
                  name="tlp-theme"
                  value={option.value}
                  checked={preference === option.value}
                  onChange={() => setPreference(option.value)}
                />
                <span className="theme-opt-icon" aria-hidden>
                  <option.icon />
                </span>
                {option.label}
              </label>
            ))}
          </div>
        </div>
        {preference === 'system' ? (
          <p className="theme-hint">
            Following your device, which is set to {mode === 'dark' ? 'dark' : 'light'}.
          </p>
        ) : null}
      </div>

      <div className="acct-actions">
        <button
          type="button"
          className="acct-action acct-action-danger"
          onClick={handleSignOut}
          aria-busy={signingOut}
        >
          <span className="acct-action-icon">
            {/* The label carries the state; the row keeps its full strength. */}
            {signingOut ? <LoadingOutlined /> : <LogoutOutlined />}
          </span>
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </div>
  );

  const trigger = (
    <button
      type="button"
      className={`acct-trigger ${collapsed ? 'acct-trigger-collapsed' : ''}`}
      /*
       * `aria-expanded` and nothing else. An `aria-haspopup="menu"` promises
       * menu keyboard semantics — arrows between items, Escape, roving focus —
       * and this popup is a radio group and a button, none of which behaves
       * that way. Claiming the role would be worse than leaving it off.
       */
      aria-expanded={open}
      aria-label={collapsed ? `Account: ${name}` : undefined}
    >
      <Avatar style={{ backgroundColor: token.colorPrimary }} size={30}>
        {user ? initials(user) : '?'}
      </Avatar>
      {collapsed ? null : (
        <>
          <span className="min-w-0 flex-1">
            <span className="acct-trigger-name">{name}</span>
            <span className="acct-trigger-role">
              {roles[0] ? roleLabel(roles[0]) : 'No roles'}
            </span>
          </span>
          <UpOutlined className="acct-caret" />
        </>
      )}
    </button>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement={collapsed ? 'topLeft' : 'topRight'}
      popupRender={() => panel}
    >
      {/* Collapsed, the name is the only thing identifying the row, so it moves
          to a tooltip rather than disappearing — same rule as a nav row. */}
      {collapsed ? (
        <Tooltip title={name} placement="right">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
    </Dropdown>
  );
}
